// S17 — staff management (spec 15 §3.1 step 5, §4 S17; M15-9, D-621). Every
// member with their role, reach, status, two-factor state and last sign-in;
// a platform_admin invites, changes a role, disables and re-enables, re-sends
// an invite and resets another member's second factor with a reason and the
// address typed out. platform_readonly reads the list and changes nothing;
// other roles are refused with a plain statement.
//
// The rules mirror the server's (lib/support.ts `staffActions`); LAST_ADMIN is
// the server's alone, because the count can change under the screen, and the
// refusal says what to do.

import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { DataTable, type DataTableColumn } from "../../../components/data-table";
import { Field } from "../../../components/field";
import { Modal } from "../../../components/modal";
import { RowMenu } from "../../../components/row-menu";
import { Select } from "../../../components/select";
import { ErrorState, ForbiddenState, TableSkeleton } from "../../../components/states";
import { StatusChip, type ChipVariant } from "../../../components/status-chip";
import { Banner } from "../../../components/toast";
import { formatTimestamp } from "../../../lib/datetime";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import { useHeldKey, withHeldKey } from "../lib/held-key";
import { listStaff, PlatformError, type PlatformMe } from "../lib/platform-api";
import {
  inviteStaff,
  resendStaffInvite,
  resetStaffTotp,
  updateStaff,
  type StaffMember,
  type StaffRole,
} from "../lib/support-api";
import {
  assignableRoles,
  canManageStaff,
  canReadStaffDirectory,
  staffActions,
  supportFailure,
} from "../lib/support";

const STAFF_KEY = ["platform", "staff"] as const;

function statusVariant(status: StaffMember["status"]): ChipVariant {
  switch (status) {
    case "active":
      return "ok";
    case "disabled":
      return "bad";
    default:
      return "warn";
  }
}

/** The sentence a refused staff write earns. */
export function staffMessage(error: unknown, t: TFunction): string {
  if (error instanceof PlatformError) {
    if (error.code === "STAFF_EXISTS") return t("admin.staffAdmin.exists");
    if (error.code === "SANDBOX_STAFF_NOT_ALLOWED") return t("admin.staffAdmin.sandboxNotAllowed");
  }
  return t(`admin.support.failure.${supportFailure(error)}`);
}

type Dialog =
  | { kind: "invite" }
  | { kind: "role"; member: StaffMember }
  | { kind: "disable"; member: StaffMember }
  | { kind: "enable"; member: StaffMember }
  | { kind: "reset"; member: StaffMember }
  | null;

export function StaffListScreen({ me }: { me: PlatformMe }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const allowed = canReadStaffDirectory(me.role);
  const manager = canManageStaff(me.role);
  const staff = useQuery({
    queryKey: STAFF_KEY,
    queryFn: ({ signal }) => listStaff(signal),
    enabled: allowed,
  });
  useSignInAgainOn401(staff.error);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [notice, setNotice] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);
  const resendKey = useHeldKey();
  const resend = useMutation({
    mutationFn: (member: StaffMember) =>
      withHeldKey(resendKey, { resend: member.id }, (key) => resendStaffInvite(member.id, key)),
    onSuccess: (member) => {
      setNotice({ kind: "ok", text: t("admin.staffAdmin.resent", { email: member.email }) });
      void queryClient.invalidateQueries({ queryKey: STAFF_KEY });
    },
    onError: (error) => {
      setNotice({ kind: "bad", text: staffMessage(error, t) });
    },
  });
  useSignInAgainOn401(resend.error);

  if (!allowed) {
    return (
      <section className="admin-screen" data-testid="admin-staff-forbidden">
        <PageHeader title={t("admin.staffAdmin.title")} />
        <ForbiddenState
          title={t("admin.staffAdmin.forbiddenTitle")}
          body={t("admin.staffAdmin.forbiddenBody")}
        />
      </section>
    );
  }

  function settle(text: string) {
    setDialog(null);
    setNotice({ kind: "ok", text });
    void queryClient.invalidateQueries({ queryKey: STAFF_KEY });
  }

  const columns: DataTableColumn<StaffMember>[] = [
    {
      key: "name",
      header: t("admin.staffAdmin.colName"),
      render: (m) => (
        <span className="admin-name-cell">
          <span className="admin-strong">{m.display_name}</span>
          <span className="admin-muted">{m.email}</span>
          {m.id === me.id ? (
            <StatusChip variant="neutral" dot={false}>
              {t("admin.staffAdmin.you")}
            </StatusChip>
          ) : null}
          {m.scope === "sandbox" ? (
            <StatusChip variant="accent" dot={false} data-testid="admin-staff-sandbox">
              {t("admin.staffAdmin.sandbox")}
            </StatusChip>
          ) : null}
        </span>
      ),
    },
    {
      key: "role",
      header: t("admin.staffAdmin.colRole"),
      render: (m) => t(`admin.roles.${m.role}`),
    },
    {
      key: "status",
      header: t("admin.staffAdmin.colStatus"),
      render: (m) => (
        <StatusChip variant={statusVariant(m.status)} data-testid="admin-staff-status">
          {t(`admin.staffAdmin.status.${m.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "totp",
      header: t("admin.staffAdmin.colTwoFactor"),
      render: (m) =>
        m.totp_confirmed ? t("admin.staffAdmin.twoFactorOn") : t("admin.staffAdmin.twoFactorOff"),
    },
    {
      key: "last",
      header: t("admin.staffAdmin.colLastLogin"),
      render: (m) =>
        m.last_login_at === null
          ? t("admin.staffAdmin.never")
          : formatTimestamp(m.last_login_at, i18n.language),
    },
  ];
  if (manager) {
    columns.push({
      key: "actions",
      header: "",
      actions: true,
      render: (m) => {
        const can = staffActions(me, m);
        return (
          <RowMenu
            label={t("admin.staffAdmin.actionsFor", { name: m.display_name })}
            data-testid="admin-staff-menu"
          >
            {can.changeRole ? (
              <button
                type="button"
                data-testid="admin-staff-role"
                onClick={() => {
                  setDialog({ kind: "role", member: m });
                }}
              >
                {t("admin.staffAdmin.changeRole")}
              </button>
            ) : null}
            {can.resendInvite ? (
              <button
                type="button"
                data-testid="admin-staff-resend"
                onClick={() => {
                  setNotice(null);
                  resend.mutate(m);
                }}
              >
                {t("admin.staffAdmin.resendInvite")}
              </button>
            ) : null}
            {can.resetTotp ? (
              <button
                type="button"
                data-testid="admin-staff-reset"
                onClick={() => {
                  setDialog({ kind: "reset", member: m });
                }}
              >
                {t("admin.staffAdmin.resetTotp")}
              </button>
            ) : null}
            {can.disable ? (
              <button
                type="button"
                data-testid="admin-staff-disable"
                onClick={() => {
                  setDialog({ kind: "disable", member: m });
                }}
              >
                {t("admin.staffAdmin.disable")}
              </button>
            ) : null}
            {can.enable ? (
              <button
                type="button"
                data-testid="admin-staff-enable"
                onClick={() => {
                  setDialog({ kind: "enable", member: m });
                }}
              >
                {t("admin.staffAdmin.enable")}
              </button>
            ) : null}
          </RowMenu>
        );
      },
    });
  }

  let body: ReactNode;
  if (staff.isPending) {
    body = <TableSkeleton rows={4} cols={5} label={t("admin.staffAdmin.loading")} />;
  } else if (staff.isError) {
    body = (
      <ErrorState
        title={t("admin.staffAdmin.loadFailed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void staff.refetch();
        }}
        requestId={requestIdOf(staff.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else {
    body = (
      <DataTable
        columns={columns}
        rows={staff.data}
        rowKey={(m) => m.id}
        empty={null}
        caption={t("admin.staffAdmin.title")}
        rowTestId="admin-staff-row"
        rowProps={(m) => ({ "data-email": m.email, "data-status": m.status })}
      />
    );
  }

  return (
    <section className="admin-screen" data-testid="admin-staff-list">
      <PageHeader
        title={t("admin.staffAdmin.title")}
        actions={
          manager ? (
            <Button
              variant="primary"
              size="sm"
              data-testid="admin-staff-invite"
              onClick={() => {
                setDialog({ kind: "invite" });
              }}
            >
              {t("admin.staffAdmin.invite")}
            </Button>
          ) : undefined
        }
      />
      <p className="admin-card-lead">{t("admin.staffAdmin.lead")}</p>
      {!manager ? (
        <Banner kind="neutral" data-testid="admin-staff-readonly">
          {t("admin.staffAdmin.readOnlyNote")}
        </Banner>
      ) : null}
      {notice !== null ? (
        <Banner
          kind={notice.kind}
          data-testid="admin-staff-notice"
          onDismiss={() => {
            setNotice(null);
          }}
          dismissLabel={t("admin.support.close")}
        >
          {notice.text}
        </Banner>
      ) : null}
      {body}
      {dialog?.kind === "invite" ? (
        <InviteDialog
          me={me}
          onClose={() => {
            setDialog(null);
          }}
          onDone={(m) => {
            settle(t("admin.staffAdmin.invited", { email: m.email }));
          }}
        />
      ) : null}
      {dialog?.kind === "role" ? (
        <RoleDialog
          me={me}
          member={dialog.member}
          onClose={() => {
            setDialog(null);
          }}
          onDone={(m) => {
            settle(
              t("admin.staffAdmin.roleChanged", {
                name: m.display_name,
                role: t(`admin.roles.${m.role}`),
              }),
            );
          }}
        />
      ) : null}
      {dialog?.kind === "disable" || dialog?.kind === "enable" ? (
        <StatusDialog
          member={dialog.member}
          to={dialog.kind === "disable" ? "disabled" : "active"}
          onClose={() => {
            setDialog(null);
          }}
          onDone={(m) => {
            settle(
              dialog.kind === "disable"
                ? t("admin.staffAdmin.disabled", { name: m.display_name })
                : t("admin.staffAdmin.enabled", { name: m.display_name, email: m.email }),
            );
          }}
        />
      ) : null}
      {dialog?.kind === "reset" ? (
        <ResetDialog
          member={dialog.member}
          onClose={() => {
            setDialog(null);
          }}
          onDone={(m) => {
            settle(t("admin.staffAdmin.reset", { name: m.display_name }));
          }}
        />
      ) : null}
    </section>
  );
}

function InviteDialog({
  me,
  onClose,
  onDone,
}: {
  me: PlatformMe;
  onClose: () => void;
  onDone: (m: StaffMember) => void;
}) {
  const { t } = useTranslation();
  const roles = assignableRoles(me);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<StaffRole>(roles[roles.length - 1] ?? "platform_readonly");
  const [scope, setScope] = useState<"fleet" | "sandbox">(me.scope);
  const key = useHeldKey();
  const body = {
    email: email.trim(),
    display_name: name.trim(),
    role,
    ...(me.scope === "fleet" ? { scope } : {}),
  };
  const invite = useMutation({
    mutationFn: () => withHeldKey(key, body, (k) => inviteStaff(body, k)),
    onSuccess: onDone,
  });
  useSignInAgainOn401(invite.error);
  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/u.test(body.email) && body.display_name !== "";
  return (
    <Modal
      label={t("admin.staffAdmin.inviteTitle")}
      heading={t("admin.staffAdmin.inviteTitle")}
      closeLabel={t("admin.support.close")}
      onClose={onClose}
      pending={invite.isPending}
      testId="admin-staff-invite-dialog"
      className="admin-modal"
    >
      <form
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) invite.mutate();
        }}
      >
        <Field label={t("admin.staffAdmin.email")} wide>
          <input
            className="ds-input"
            type="email"
            value={email}
            autoComplete="off"
            data-testid="admin-staff-invite-email"
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
        </Field>
        <Field label={t("admin.staffAdmin.name")} wide>
          <input
            className="ds-input"
            value={name}
            maxLength={200}
            data-testid="admin-staff-invite-name"
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </Field>
        <Field label={t("admin.staffAdmin.role")}>
          <Select
            value={role}
            data-testid="admin-staff-invite-role"
            onChange={(e) => {
              setRole(e.target.value as StaffRole);
            }}
          >
            {roles.map((r) => (
              <option key={r} value={r}>
                {t(`admin.roles.${r}`)}
              </option>
            ))}
          </Select>
        </Field>
        {me.scope === "fleet" ? (
          <Field label={t("admin.staffAdmin.scope")}>
            <Select
              value={scope}
              data-testid="admin-staff-invite-scope"
              onChange={(e) => {
                setScope(e.target.value as "fleet" | "sandbox");
              }}
            >
              <option value="fleet">{t("admin.staffAdmin.scopes.fleet")}</option>
              <option value="sandbox">{t("admin.staffAdmin.scopes.sandbox")}</option>
            </Select>
          </Field>
        ) : null}
        {invite.isError ? (
          <Banner kind="bad" data-testid="admin-staff-invite-error">
            {staffMessage(invite.error, t)}
          </Banner>
        ) : null}
        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={!valid}
            busy={invite.isPending}
            busyLabel={t("admin.staffAdmin.inviting")}
            data-testid="admin-staff-invite-send"
          >
            {t("admin.staffAdmin.inviteSend")}
          </Button>
          <Button onClick={onClose}>{t("admin.support.cancel")}</Button>
        </div>
      </form>
    </Modal>
  );
}

function RoleDialog({
  me,
  member,
  onClose,
  onDone,
}: {
  me: PlatformMe;
  member: StaffMember;
  onClose: () => void;
  onDone: (m: StaffMember) => void;
}) {
  const { t } = useTranslation();
  const [role, setRole] = useState<StaffRole>(member.role);
  const key = useHeldKey();
  const save = useMutation({
    mutationFn: () =>
      withHeldKey(key, { id: member.id, role }, (k) => updateStaff(member.id, { role }, k)),
    onSuccess: onDone,
  });
  useSignInAgainOn401(save.error);
  return (
    <Modal
      label={t("admin.staffAdmin.roleTitle", { name: member.display_name })}
      heading={t("admin.staffAdmin.roleTitle", { name: member.display_name })}
      closeLabel={t("admin.support.close")}
      onClose={onClose}
      pending={save.isPending}
      testId="admin-staff-role-dialog"
      className="admin-modal"
    >
      <form
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (role !== member.role) save.mutate();
        }}
      >
        <Field label={t("admin.staffAdmin.role")}>
          <Select
            value={role}
            data-testid="admin-staff-role-select"
            onChange={(e) => {
              setRole(e.target.value as StaffRole);
            }}
          >
            {assignableRoles(me).map((r) => (
              <option key={r} value={r}>
                {t(`admin.roles.${r}`)}
              </option>
            ))}
          </Select>
        </Field>
        {save.isError ? (
          <Banner kind="bad" data-testid="admin-staff-role-error">
            {staffMessage(save.error, t)}
          </Banner>
        ) : null}
        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={role === member.role}
            busy={save.isPending}
            busyLabel={t("admin.staffAdmin.working")}
            data-testid="admin-staff-role-save"
          >
            {t("admin.staffAdmin.roleSave")}
          </Button>
          <Button onClick={onClose}>{t("admin.support.cancel")}</Button>
        </div>
      </form>
    </Modal>
  );
}

function StatusDialog({
  member,
  to,
  onClose,
  onDone,
}: {
  member: StaffMember;
  to: "active" | "disabled";
  onClose: () => void;
  onDone: (m: StaffMember) => void;
}) {
  const { t } = useTranslation();
  const key = useHeldKey();
  const save = useMutation({
    mutationFn: () =>
      withHeldKey(key, { id: member.id, status: to }, (k) =>
        updateStaff(member.id, { status: to }, k),
      ),
    onSuccess: onDone,
  });
  useSignInAgainOn401(save.error);
  const disabling = to === "disabled";
  const title = disabling
    ? t("admin.staffAdmin.disableTitle", { name: member.display_name })
    : t("admin.staffAdmin.enableTitle", { name: member.display_name });
  return (
    <Modal
      label={title}
      heading={title}
      closeLabel={t("admin.support.close")}
      onClose={onClose}
      pending={save.isPending}
      testId="admin-staff-status-dialog"
      className="admin-modal"
    >
      <p className="admin-card-lead">
        {disabling ? t("admin.staffAdmin.disableBody") : t("admin.staffAdmin.enableBody")}
      </p>
      {save.isError ? (
        <Banner kind="bad" data-testid="admin-staff-status-error">
          {staffMessage(save.error, t)}
        </Banner>
      ) : null}
      <div className="admin-actions">
        <Button
          variant={disabling ? "danger" : "primary"}
          busy={save.isPending}
          busyLabel={t("admin.staffAdmin.working")}
          data-testid="admin-staff-status-confirm"
          onClick={() => {
            save.mutate();
          }}
        >
          {disabling ? t("admin.staffAdmin.disable") : t("admin.staffAdmin.enable")}
        </Button>
        <Button onClick={onClose}>{t("admin.support.cancel")}</Button>
      </div>
    </Modal>
  );
}

function ResetDialog({
  member,
  onClose,
  onDone,
}: {
  member: StaffMember;
  onClose: () => void;
  onDone: (m: StaffMember) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const key = useHeldKey();
  const body = { reason: reason.trim() };
  const reset = useMutation({
    mutationFn: () =>
      withHeldKey(key, { id: member.id, ...body }, (k) =>
        resetStaffTotp(member.id, body.reason, k),
      ),
    onSuccess: onDone,
  });
  useSignInAgainOn401(reset.error);
  const valid =
    body.reason.length >= 5 && typed.trim().toLowerCase() === member.email.toLowerCase();
  return (
    <Modal
      label={t("admin.staffAdmin.resetTitle", { name: member.display_name })}
      heading={t("admin.staffAdmin.resetTitle", { name: member.display_name })}
      closeLabel={t("admin.support.close")}
      onClose={onClose}
      pending={reset.isPending}
      testId="admin-staff-reset-dialog"
      className="admin-modal"
    >
      <form
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) reset.mutate();
        }}
      >
        <p className="admin-card-lead">{t("admin.staffAdmin.resetBody")}</p>
        <Field label={t("admin.staffAdmin.resetReason")} wide>
          <input
            className="ds-input"
            value={reason}
            maxLength={500}
            data-testid="admin-staff-reset-reason"
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </Field>
        <p className="admin-muted">{t("admin.staffAdmin.resetReasonHint")}</p>
        <Field label={t("admin.staffAdmin.resetConfirmLabel", { email: member.email })} wide>
          <input
            className="ds-input"
            value={typed}
            autoComplete="off"
            data-testid="admin-staff-reset-typed"
            onChange={(e) => {
              setTyped(e.target.value);
            }}
          />
        </Field>
        {reset.isError ? (
          <Banner kind="bad" data-testid="admin-staff-reset-error">
            {staffMessage(reset.error, t)}
          </Banner>
        ) : null}
        <div className="admin-actions">
          <Button
            type="submit"
            variant="danger"
            disabled={!valid}
            busy={reset.isPending}
            busyLabel={t("admin.staffAdmin.working")}
            data-testid="admin-staff-reset-confirm"
          >
            {t("admin.staffAdmin.resetTotp")}
          </Button>
          <Button onClick={onClose}>{t("admin.support.cancel")}</Button>
        </div>
      </form>
    </Modal>
  );
}
